import React from 'react';
import dayjs from 'dayjs';
import {
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  IconButton,
  Tooltip,
  Chip,
  Skeleton,
  Switch,
  Button,
  Stack,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import HistoryIcon from '@mui/icons-material/History';
import AddCircleOutlineRoundedIcon from '@mui/icons-material/AddCircleOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import { RECUR_TYPE_LABEL } from './constants';
import { formatRecurDate, formatRangeLabel, formatPeriodLabel } from './utils';

// สถานะงวดที่ต้องกดสร้าง (มาจาก backend computePeriods) — overdue = เลยวันที่รับเงินแล้ว, due = อยู่ในช่วงเตือน
const isPending = (p) => p.status === 'due' || p.status === 'overdue';

function PeriodCell({ row, onCreate }) {
  const periods = row.periods || [];
  const pending = periods.filter(isPending);
  const created = periods.filter((p) => p.status === 'created');
  const lastCreated = created[created.length - 1];
  const nextUpcoming = periods.find((p) => p.status === 'upcoming');

  return (
    <Stack spacing={0.75} alignItems="flex-start">
      {pending.map((p) => (
        <Stack key={p.period} direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="contained"
            color={p.status === 'overdue' ? 'error' : 'warning'}
            startIcon={<AddCircleOutlineRoundedIcon fontSize="small" />}
            onClick={() => onCreate(row, p)}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', py: 0.25 }}
          >
            สร้าง {formatPeriodLabel(row.recur_type, p.period)}
          </Button>
          <Typography variant="caption" color={p.status === 'overdue' ? 'error' : 'text.secondary'}>
            {p.status === 'overdue' ? 'เลยกำหนด ' : 'รับเงิน '}
            {dayjs(p.due_date).format('DD/MM/YYYY')}
          </Typography>
        </Stack>
      ))}
      {pending.length === 0 && lastCreated && (
        <Chip
          size="small"
          color="success"
          variant="outlined"
          icon={<CheckCircleRoundedIcon />}
          label={`${formatPeriodLabel(row.recur_type, lastCreated.period)} สร้างแล้ว`}
        />
      )}
      {pending.length === 0 && nextUpcoming && row.active !== false && (
        <Typography variant="caption" color="text.secondary">
          งวดถัดไป {formatPeriodLabel(row.recur_type, nextUpcoming.period)} · เริ่มเตือน {dayjs(nextUpcoming.remind_from).format('DD/MM/YYYY')}
        </Typography>
      )}
      {row.active === false && (
        <Typography variant="caption" color="text.secondary">ปิดการเตือนอยู่</Typography>
      )}
    </Stack>
  );
}

// ตาราง list ล้วนๆ — ไม่ถือ state/fetch เอง รับ rows + handler มาจาก parent (ExpensesRecurringManage.js)
export default function RecurringTable({ rows, loading, onEdit, onDelete, onHistory, onToggleActive, onCreate }) {
  return (
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>No.</TableCell>
            <TableCell>รายการ</TableCell>
            <TableCell>ประเภท</TableCell>
            <TableCell>วันที่รับเงิน</TableCell>
            <TableCell>โครงการ/งบประมาณ</TableCell>
            <TableCell>งวดที่ต้องสร้าง</TableCell>
            <TableCell align="center">เตือน</TableCell>
            <TableCell align="right">จัดการ</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <TableRow>
              <TableCell colSpan={8}><Skeleton /></TableCell>
            </TableRow>
          )}
          {!loading && rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={8}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  ยังไม่มีรายการเบิกประจำ
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {rows.map((row, index) => (
            <TableRow key={row._id} hover sx={{ opacity: row.active === false ? 0.55 : 1 }}>
              <TableCell>{index + 1}</TableCell>
              <TableCell sx={{ maxWidth: 320 }}>{row.name}</TableCell>
              <TableCell>
                <Chip size="small" label={RECUR_TYPE_LABEL[row.recur_type] || row.recur_type} />
              </TableCell>
              <TableCell>
                <Typography variant="body2">{formatRecurDate(row.recur_type, row.date)}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  เตือนล่วงหน้า {row.remind_days_before ?? 7} วัน
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatRangeLabel(row.recur_type, row.range_start, row.range_end)}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2">
                  {row.project ? `${row.project.project_number ? `${row.project.project_number} | ` : ''}${row.project.name || ''}` : '-'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.budget ? `${row.budget.prefix || ''}${row.budget.budget_number || ''} ${row.budget.name || ''}`.trim() : '-'}
                </Typography>
              </TableCell>
              <TableCell>
                <PeriodCell row={row} onCreate={onCreate} />
              </TableCell>
              <TableCell align="center">
                <Tooltip title={row.active === false ? 'ปิดอยู่ — ไม่เตือนงวดถัดไป' : 'เปิดอยู่ — เตือนเมื่อใกล้ถึงวันที่รับเงิน'}>
                  <Switch
                    size="small"
                    checked={row.active !== false}
                    onChange={() => onToggleActive(row)}
                  />
                </Tooltip>
              </TableCell>
              <TableCell align="right">
                <Tooltip title="ดูประวัติที่เคยสร้าง">
                  <IconButton size="small" onClick={() => onHistory(row)}>
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="แก้ไข">
                  <IconButton size="small" onClick={() => onEdit(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="ลบ">
                  <IconButton size="small" onClick={() => onDelete(row)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
